import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Database } from '@/lib/supabase/supabase-types';
import { toast } from 'sonner';
import { freelancerSkillService } from '@/lib/api/services/freelancer-skill.service';

type FreelancerSkill = Database['public']['Tables']['freelancer_skills']['Row'];
type FreelancerSkillInsert = Database['public']['Tables']['freelancer_skills']['Insert'];
type FreelancerSkillUpdate = Database['public']['Tables']['freelancer_skills']['Update'];

export function useFreelancerSkills(includeInactive = false) {
  return useQuery({
    queryKey: ['freelancer-skills', includeInactive],
    queryFn: async () => {
      return await freelancerSkillService.getSkills(includeInactive);
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateFreelancerSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (skill: FreelancerSkillInsert) => {
      return await freelancerSkillService.createSkill(skill);
    },
    onMutate: async (newSkill) => {
      await queryClient.cancelQueries({ queryKey: ['freelancer-skills'] });

      const previousSkills = queryClient.getQueryData<FreelancerSkill[]>(['freelancer-skills', true]);

      if (previousSkills) {
        const optimisticSkill: FreelancerSkill = {
          id: crypto.randomUUID(),
          name: newSkill.name!,
          category: newSkill.category || null,
          is_active: newSkill.is_active ?? true,
          display_order: newSkill.display_order || null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        queryClient.setQueryData<FreelancerSkill[]>(
          ['freelancer-skills', true],
          [...previousSkills, optimisticSkill]
        );
      }

      return { previousSkills };
    },
    onError: (_err, _newSkill, context) => {
      if (context?.previousSkills) {
        queryClient.setQueryData(['freelancer-skills', true], context.previousSkills);
      }
      toast.error('Failed to create skill');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['freelancer-skills'] });
      toast.success('Skill created successfully');
    },
  });
}

export function useUpdateFreelancerSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: FreelancerSkillUpdate & { id: string }) => {
      return await freelancerSkillService.updateSkill(id, updates);
    },
    onMutate: async (updatedSkill) => {
      await queryClient.cancelQueries({ queryKey: ['freelancer-skills'] });

      const previousSkills = queryClient.getQueryData<FreelancerSkill[]>(['freelancer-skills', true]);

      if (previousSkills) {
        queryClient.setQueryData<FreelancerSkill[]>(
          ['freelancer-skills', true],
          previousSkills.map((skill) =>
            skill.id === updatedSkill.id
              ? { ...skill, ...updatedSkill, updated_at: new Date().toISOString() }
              : skill
          )
        );
      }

      return { previousSkills };
    },
    onError: (_err, _updatedSkill, context) => {
      if (context?.previousSkills) {
        queryClient.setQueryData(['freelancer-skills', true], context.previousSkills);
      }
      toast.error('Failed to update skill');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['freelancer-skills'] });
      toast.success('Skill updated successfully');
    },
  });
}

export function useDeleteFreelancerSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await freelancerSkillService.deleteSkill(id);
    },
    onMutate: async (deletedId) => {
      await queryClient.cancelQueries({ queryKey: ['freelancer-skills'] });

      const previousSkills = queryClient.getQueryData<FreelancerSkill[]>(['freelancer-skills', true]);

      if (previousSkills) {
        queryClient.setQueryData<FreelancerSkill[]>(
          ['freelancer-skills', true],
          previousSkills.filter((skill) => skill.id !== deletedId)
        );
      }

      return { previousSkills };
    },
    onError: (_err, _deletedId, context) => {
      if (context?.previousSkills) {
        queryClient.setQueryData(['freelancer-skills', true], context.previousSkills);
      }
      toast.error('Failed to delete skill');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['freelancer-skills'] });
      toast.success('Skill deleted successfully');
    },
  });
}

export function useReorderFreelancerSkills() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (skills: Array<{ id: string; display_order: number }>) => {
      await freelancerSkillService.reorderSkills(skills);
    },
    onMutate: async (reorderedSkills) => {
      await queryClient.cancelQueries({ queryKey: ['freelancer-skills'] });

      const previousSkills = queryClient.getQueryData<FreelancerSkill[]>(['freelancer-skills', true]);

      if (previousSkills) {
        const reorderMap = new Map(reorderedSkills.map(s => [s.id, s.display_order]));

        const updatedSkills = previousSkills.map(skill => ({
          ...skill,
          display_order: reorderMap.get(skill.id) ?? skill.display_order
        })).sort((a, b) => {
          const orderA = a.display_order ?? Number.MAX_VALUE;
          const orderB = b.display_order ?? Number.MAX_VALUE;
          return orderA - orderB;
        });

        queryClient.setQueryData<FreelancerSkill[]>(
          ['freelancer-skills', true],
          updatedSkills
        );
      }

      return { previousSkills };
    },
    onError: (_err, _reorderedSkills, context) => {
      if (context?.previousSkills) {
        queryClient.setQueryData(['freelancer-skills', true], context.previousSkills);
      }
      toast.error('Failed to reorder skills');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['freelancer-skills'] });
      toast.success('Skills reordered successfully');
    },
  });
}
